import { useEffect, useRef } from 'react'
import { setupHighDPICanvas, renderFairlightWaveform } from '../utils/waveformRenderer'

interface WaveformViewProps {
  buffer: AudioBuffer | null
  playheadTime: number | null
}

const MAX_RETRIES = 10

// Helper function to resolve CSS variables to computed color values
function getCSSVariable(element: HTMLElement, variableName: string): string {
  const value = getComputedStyle(element).getPropertyValue(variableName).trim()
  return value || variableName // Fallback to variable name if not found
}

export function WaveformView({ buffer, playheadTime }: WaveformViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const retryCountRef = useRef(0)
  const hasDrawnRef = useRef(false)

  const viewStart = 0
  const viewEnd = buffer ? buffer.duration : 0
  const viewDuration = Math.max(0.0001, viewEnd - viewStart)

  let playheadPercent: number | null = null
  if (buffer && playheadTime !== null) {
    if (playheadTime >= viewStart && playheadTime <= viewEnd) {
      playheadPercent = ((playheadTime - viewStart) / viewDuration) * 100
    }
  }

  useEffect(() => {
    if (!buffer) return

    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return

    let isDrawing = false
    let rafId: number | null = null

    const drawWaveform = () => {
      if (!container || !canvas || !buffer || isDrawing) return
      
      const rect = container.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1
          rafId = requestAnimationFrame(() => {
            rafId = requestAnimationFrame(() => drawWaveform())
          })
        }
        return
      }

      isDrawing = true
      const ctx = setupHighDPICanvas(canvas, rect.width, rect.height)
      if (!ctx) {
        isDrawing = false
        return
      }

      // Resolve CSS variables to actual color values
      const backgroundColor = getCSSVariable(container, '--wave-bg')
      const outlineColor = getCSSVariable(container, '--wave-outline')
      const peakColor = getCSSVariable(container, '--wave-peak')
      const coreGradient = {
        top: getCSSVariable(container, '--wave-grad-top'),
        middle: getCSSVariable(container, '--wave-grad-mid'),
        bottom: getCSSVariable(container, '--wave-grad-bottom'),
      }

      renderFairlightWaveform(ctx, buffer, {
        width: rect.width,
        height: rect.height,
        viewStart,
        viewEnd,
        backgroundColor,
        outlineColor,
        peakColor,
        coreGradient,
      })

      hasDrawnRef.current = true
      retryCountRef.current = 0
      isDrawing = false
    }

    const scheduleDraw = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          rafId = requestAnimationFrame(() => {
            drawWaveform()
            rafId = null
          })
        })
      })
    }

    scheduleDraw()

    let resizeTimeout: number | null = null
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout !== null) {
        clearTimeout(resizeTimeout)
      }
      resizeTimeout = window.setTimeout(() => {
        if (!container || !canvas) return
        const rect = container.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          scheduleDraw()
        }
        resizeTimeout = null
      }, 100) // Debounce resize events
    })
    resizeObserver.observe(container)

    const intersectionObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting && !hasDrawnRef.current) {
            scheduleDraw()
          }
        }
      },
      { threshold: 0.1 },
    )
    intersectionObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
      intersectionObserver.disconnect()
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      if (resizeTimeout !== null) {
        clearTimeout(resizeTimeout)
      }
      hasDrawnRef.current = false
      isDrawing = false
    }
  }, [buffer, viewStart, viewEnd])

  return (
    <div
      ref={containerRef}
      style={{ 
        width: '100%', 
        height: '100%', 
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      {playheadPercent !== null && (
        <div
          className="waveform-playhead"
          style={{ left: `${playheadPercent}%` }}
        />
      )}
    </div>
  )
}
